<template>
  <div v-click-outside="closeRubricList">
    <div
      class="settings__toggle"
      :class="{ 
        'settings__toggle--active': showRubricList,
        'settings__toggle--clicable': filtredCategoriesLength()
      }"
      @click="toggleRubric"
    >
      <template v-if="selectedChart === 0">
        {{ t('news_page_themes_edit_popup.label.theme') }}
      </template>
      <template v-else-if="selectedChart === 1">
        {{ t('news_page_themes_edit_popup.label.rubric') }}
      </template>
      <template v-else-if="selectedChart === 2">
        {{ t('news_page_body_post.label.category') }}
      </template>
      ({{ filtredCategoriesLength() || 0 }})
      <ArrowBottomIcon
        v-if="categories.length"
        class="settings__toggle-svg"
        :class="{ 'settings__toggle-svg--active' : showRubricList }"
      />
    </div>
    <div
      v-if="showRubricList"
      class="settings-modal"
    >
      <ChartChooser
        v-model="selectedChart"
        name="FilterSelectorChart"
        @before-update="beforeChartUpdate"
      />
      <AvalListView class="scroll settings-modal__list">
        <AvalListItem
          v-for="item in categories"
          :key="item.name"
          v-model="selectedRubric"
          :use-checkbox="true"
          :item="item.name"
        >
          {{ item.name }}
        </AvalListItem>
      </AvalListView>

      <AvalButtonAccentPrimary @click="updateRubrics">
        {{ t('sentiment_stat_bar.apply') }}
      </AvalButtonAccentPrimary>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, nextTick, onMounted, ref, toRaw, watch } from "vue";
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import { t } from "@/controllerLK/AvalLocalization";
import ChartChooser from "./ChartChooser.vue";
import ArrowBottomIcon from "@/components/Icons/ArrowBottomIcon.vue";

const emit = defineEmits(["updateRubrics", "update:selectedChart", "clearRubrics"]);

const props = defineProps({
  selectedChart: {
    type: Number,
    required: true
  },
  activeStore: {
    type: Object,
    required: true
  },
  date: {
    type: Array,
    required: false,
    default: () => []
  },
  active: {
    type: Boolean,
    default: true
  },
  stopLoadOnClear: {
    type: Boolean,
    default: false
  }
});

const rubricsStore = useRubricsStore();

const showRubricList = ref(false);
const selectedRubric = ref([]);
const selectedChart = ref(props.selectedChart);

watch(
  () => selectedChart.value,
  (newVal) => {
    emit("update:selectedChart", newVal);
  }
);

watch(
  () => props.selectedChart,
  (newVal) => {
    selectedChart.value = newVal;
  }
);

watch(
  () => selectedRubric.value,
  (val) => {
    if (!props.stopLoadOnClear && val?.length === 0) fillSelectedRubrics();
  },
);

watch(
  [() => selectedChart.value, () => props.activeStore?.items?.length, props.date],
  () => {
    setTimeout(async () => {
      if (!categories.value.length) return;
      await fillSelectedRubrics();
      setTimeout(() => {
        if (emit) emit("updateRubrics", toRaw(selectedRubric.value));
      }, 0);
    }, 0);
  },
);

const categories = computed(() => {
  if (selectedChart.value === 1) return rubricsStore.allRubrics.filter(r => r?.is_tracked);
  if (!props.activeStore?.items?.length) return [];
  return props.activeStore.items.filter(item =>
    item.data != null && typeof item.data.count !== undefined
  );
});

const filtredCategoriesLength = () => {
  if (!props.active) return 0;
  if (selectedRubric?.value?.length) return selectedRubric.value.length;
  return categories.value.length;
};

const closeRubricList  = () => {
  showRubricList.value = false;
};

const beforeChartUpdate = () => {};

const fillSelectedRubrics = async () => {
  // Получаем обычный массив без реактивности
  const rawCategories = toRaw(categories.value);

  // Формируем список ID активных категорий
  const activeIDs =
    props?.activeStore?.categories?.length ?
      props.activeStore.categories :
      rawCategories.map((item) => item.name);

  // Проверяем, изменилось ли значение, чтобы избежать лишнего рендера
  if (JSON.stringify(selectedRubric.value) !== JSON.stringify(activeIDs)) {
    await nextTick(); // Подождём завершения текущего рендера
    selectedRubric.value = activeIDs;
  }
};

const updateRubrics = () => {
  const selected = toRaw(selectedRubric.value);

  if (selected.length === 0) {
    emit("clearRubrics");
    if (props.stopLoadOnClear) {
      showRubricList.value = false;
      return;
    }
  }

  if (typeof emit === "function") emit("updateRubrics", selected, true);

  if (!props.stopLoadOnClear && selectedRubric.value.length === categories.value.length) selectedRubric.value = [];
  showRubricList.value = false;
};

const toggleRubric = () => {
  if (props.active) showRubricList.value = !showRubricList.value;
};

onMounted(() => {
  nextTick(async () => {
    await fillSelectedRubrics();
  });
});
</script>

<style lang="scss" scoped>
.settings {

  &__toggle {
    display: flex;
    gap: 5px;
    align-items: center;
    z-index: 30;
    background: var(--background-01);
    padding: 14px;
    border-radius: 8px;
    user-select: none;
    width: 135px; // По макету width: 130px. Не влазиет в одну строку, если выбрано > 9 тем.

    &-svg {
      color: var(--text-text-pur);
      transition: .2s;
      margin-left: auto;

      &--active {
        transform: rotate(180deg);
        color: #1F9AFF;
      }
    }

    &--active {
      color: #1F9AFF;
      border: #EEF1FF;
    }

    &--clicable {
      cursor: pointer;
    }
  }

  &-modal {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 200px;
    max-width: 330px;
    max-height: 379px;
    overflow-x: hidden;
    background: var(--background-bg-white);
    z-index: 40;
    position: absolute;
    top: 45px;
    right: 26px;
    padding: 10px;
    border-radius: 8px;
    box-shadow: 0 9px 28px 8px #0000001a;
    line-height: 18px;
    text-align: left;
    user-select: none;

    &__list {
      max-height: 290px;
    }
  }
}
</style>